// frontend/src/pages/reports/DemographicsReportPage.js
import React, { useState, useEffect } from 'react';
import {
  Table, Card, Space, Button, Select, Spin, Typography, Row, Col,
  Statistic, Divider, Empty, Tag, message, Tabs, Checkbox
} from 'antd';
import { DownloadOutlined, PrinterOutlined } from '@ant-design/icons';
import { getAllStudents } from '../../services/studentService';
import { getAllClasses } from '../../services/classService';
import { useAcademicYear } from '../../context/AcademicYearContext';

const { Title, Text } = Typography;

const DemographicsReportPage = () => {
  const { currentYear } = useAcademicYear();
  const [students, setStudents] = useState([]);
  const [classes, setClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState(null);
  const [selectedGender, setSelectedGender] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedPovertyStatus, setSelectedPovertyStatus] = useState(null);
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pageSize, setPageSize] = useState(15);
  const [stats, setStats] = useState({ male: 0, female: 0, scst: 0, bpl: 0 });
  const [reportType, setReportType] = useState('all');

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [studentsData, classesData] = await Promise.all([
          getAllStudents(),
          getAllClasses()
        ]);
        setStudents(studentsData);
        setClasses(classesData);
        // console.log('DemographicsReportPage - Fetched students:', studentsData);
        // console.log('DemographicsReportPage - Fetched classes:', classesData);

        // Calculate base stats
        const male = studentsData.filter(s => s.gender === 'male').length;
        const female = studentsData.filter(s => s.gender === 'female').length;
        const scst = studentsData.filter(s => s.allotted_category === 'SC' || s.allotted_category === 'ST').length;
        const bpl = studentsData.filter(s => s.ration_card_type === 'BPL').length;

        setStats({ male, female, scst, bpl });
        // console.log('DemographicsReportPage - Calculated Stats:', { male, female, scst, bpl });
      } catch (error) {
        message.error('Failed to load students');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [currentYear]);

  // Apply filters
  useEffect(() => {
    let filtered = [...students];

    if (selectedClass) {
      filtered = filtered.filter(s => s.class_id === selectedClass);
    }
    if (selectedGender) {
      filtered = filtered.filter(s => s.gender === selectedGender);
    }
    if (selectedCategory) {
      filtered = filtered.filter(s => s.allotted_category === selectedCategory);
    }
    if (selectedPovertyStatus) {
      filtered = filtered.filter(s => s.ration_card_type === selectedPovertyStatus);
    }

    // console.log('DemographicsReportPage - Applied filters:', { selectedClass, selectedGender, selectedCategory, selectedPovertyStatus });
    // console.log('DemographicsReportPage - Filtered results count:', filtered.length);
    setFilteredStudents(filtered);
  }, [selectedClass, selectedGender, selectedCategory, selectedPovertyStatus, students]);

  const columns = [
    {
      title: 'Admission No',
      dataIndex: 'admission_no',
      key: 'admission_no',
      width: 120,
    },
    {
      title: 'Student Name',
      dataIndex: 'student_name',
      key: 'student_name',
      filterSearch: true,
    },
    {
      title: 'Class',
      dataIndex: 'class_name',
      key: 'class_name',
      width: 80,
    },
    {
      title: 'Gender',
      dataIndex: 'gender',
      key: 'gender',
      render: (gender) => (
        <Tag color={gender === 'male' ? 'blue' : gender === 'female' ? 'pink' : 'default'}>
          {gender || 'N/A'}
        </Tag>
      ),
      width: 80,
    },
    {
      title: 'Category',
      key: 'category',
      render: (_, record) => {
        const cat = record.allotted_category;
        if (cat === 'SC' || cat === 'ST') {
          return <Tag color="orange">{cat}</Tag>;
        }
        return <Tag>{cat || 'General'}</Tag>;
      },
      width: 80,
    },
    {
      title: 'Poverty Status',
      dataIndex: 'ration_card_type',
      key: 'ration_card_type',
      render: (status) => (
        <Tag color={status === 'BPL' ? 'red' : status === 'APL' ? 'green' : 'default'}>
          {status || 'Not Specified'}
        </Tag>
      ),
      width: 100,
    },
  ];

  const handleExport = () => {
    if (filteredStudents.length === 0) {
      message.warning('No data to export');
      return;
    }

    let title = 'Demographics Report';
    let filters = [];
    if (selectedClass) filters.push(`Class: ${classes.find(c => c.id === selectedClass)?.name}`);
    if (selectedGender) filters.push(`Gender: ${selectedGender}`);
    if (selectedCategory) filters.push(`Category: ${selectedCategory}`);
    if (selectedPovertyStatus) filters.push(`Poverty: ${selectedPovertyStatus}`);

    const csv = [
      [title],
      ...filters.map(f => [f]),
      [''],
      ['Admission No', 'Student Name', 'Class', 'Gender', 'Category', 'Poverty Status'],
      ...filteredStudents.map(s => [
        s.admission_no,
        s.student_name,
        s.class_name || '-',
        s.gender || '-',
        s.allotted_category || 'General',
        s.ration_card_type || 'Not Specified',
      ])
    ]
      .map(row => row.join(','))
      .join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `demographics_report.csv`;
    a.click();
  };

  const genderData = [
    { name: 'Male', value: stats.male, color: '#1890ff' },
    { name: 'Female', value: stats.female, color: '#ff85c0' },
  ];

  const categoryData = [
    { name: 'SC/ST', value: stats.scst, color: '#ff7a45' },
    { name: 'General', value: stats.male + stats.female - stats.scst, color: '#52c41a' },
  ];

  const povertyData = [
    { name: 'BPL', value: stats.bpl, color: '#ff4d4f' },
    { name: 'APL', value: stats.male + stats.female - stats.bpl, color: '#52c41a' },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Space direction="vertical" style={{ width: '100%' }} size="large">
        <div>
          <Title level={2}>👥 Demographics & Special Categories Report</Title>
          <Text type="secondary">
            View student reports filtered by gender, caste, and poverty status
          </Text>
        </div>

        {/* Summary Statistics */}
        <Row gutter={16}>
          <Col xs={12} sm={8}>
            <Card>
              <Statistic
                title="Total Students"
                value={students.length}
                valueStyle={{ color: '#1890ff' }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={8}>
            <Card>
              <Statistic
                title="Male Students"
                value={stats.male}
                valueStyle={{ color: '#1890ff' }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={8}>
            <Card>
              <Statistic
                title="Female Students"
                value={stats.female}
                valueStyle={{ color: '#ff85c0' }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={8}>
            <Card>
              <Statistic
                title="SC/ST Students"
                value={stats.scst}
                valueStyle={{ color: '#ff7a45' }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={8}>
            <Card>
              <Statistic
                title="BPL Students"
                value={stats.bpl}
                valueStyle={{ color: '#ff4d4f' }}
              />
            </Card>
          </Col>
        </Row>

        <Divider />

        {/* Filters */}
        <Card title="Filters" extra={<Text type="secondary">Select filters to refine the report</Text>}>
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} lg={6}>
              <label>Filter by Class</label>
              <Select
                placeholder="All Classes"
                value={selectedClass}
                onChange={setSelectedClass}
                allowClear
                options={[
                  { label: 'All Classes', value: null },
                  ...classes.map(c => ({
                    label: c.name,
                    value: c.id,
                  }))
                ]}
                style={{ width: '100%' }}
              />
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <label>Filter by Gender</label>
              <Select
                placeholder="All Genders"
                value={selectedGender}
                onChange={setSelectedGender}
                allowClear
                options={[
                  { label: 'Male', value: 'male' },
                  { label: 'Female', value: 'female' },
                ]}
                style={{ width: '100%' }}
              />
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <label>Filter by Category</label>
              <Select
                placeholder="All Categories"
                value={selectedCategory}
                onChange={setSelectedCategory}
                allowClear
                options={[
                  { label: 'General', value: 'General' },
                  { label: 'OBC', value: 'OBC' },
                  { label: 'SC', value: 'SC' },
                  { label: 'ST', value: 'ST' },
                ]}
                style={{ width: '100%' }}
              />
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <label>Filter by Poverty Status</label>
              <Select
                placeholder="All Status"
                value={selectedPovertyStatus}
                onChange={setSelectedPovertyStatus}
                allowClear
                options={[
                  { label: 'APL', value: 'APL' },
                  { label: 'BPL', value: 'BPL' },
                ]}
                style={{ width: '100%' }}
              />
            </Col>
          </Row>
        </Card>

        {/* Results */}
        <Card
          title={`Students Report (${filteredStudents.length} results)`}
          extra={
            <Space>
              <Button
                type="primary"
                icon={<DownloadOutlined />}
                onClick={handleExport}
                disabled={filteredStudents.length === 0}
              >
                Export CSV
              </Button>
              <Button icon={<PrinterOutlined />} onClick={() => window.print()}>
                Print
              </Button>
            </Space>
          }
        >
          {loading ? (
            <Spin />
          ) : filteredStudents.length > 0 ? (
            <Table
              columns={columns}
              dataSource={filteredStudents.map((s, i) => ({ ...s, key: i }))}
              pagination={{
                pageSize: pageSize,
                showSizeChanger: true,
                pageSizeOptions: [10, 20, 50, 100],
                onShowSizeChange: (current, size) => setPageSize(size),
                onChange: (page, size) => setPageSize(size)
              }}
              scroll={{ x: 1000 }}
            />
          ) : (
            <Empty description="No students found with selected filters" />
          )}
        </Card>
      </Space>
    </div>
  );
};

export default DemographicsReportPage;
